import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { FormattedNumber, useIntl } from "react-intl";
import { Grid, Typography, Button, CircularProgress } from "@material-ui/core";
import { ErrorDisplay } from "@ploy-lib/types";
import { useState } from "react";
import { MuiAutocompleteField } from "@ploy-ui/form-fields";
import { ChangeContractSettingsEndpoint } from "./endpoints/ChangeContractSettings";
import { LabeledProperty } from "./LabeledProperty";
import { messages } from "./messages";
import { Endpoint, useResource } from "rest-hooks";
import { useNavigate } from "react-router-dom";
import { transformContractToApplication } from "./endpoints/TransformContractToApplication";
import { typeOfChangeChanged } from "./endpoints/TypeOfChangeChanged";
const ChangeContractValidation = () => object().shape({
    typeOfChange: string().required("Must select type of change")
});
const fetchTypesOfChange = () => fetch("/changeCustomer/getTypesOfChange").then(r => r.json());
const TypesOfChangeEndpoint = new Endpoint(fetchTypesOfChange, {
    dataExpiryLength: 60 * 60 * 1000
});
const fetchLegalProducts = (data) => fetch(`/changeCustomer/getContractChangeProducts?product=${encodeURIComponent(data.productFromContract)}`).then(r => r.json());
const LegalProductsEndpoint = new Endpoint(fetchLegalProducts, {
    key(data) {
        return `LOAD legal-products ${data.productFromContract}`;
    },
    dataExpiryLength: 60 * 1000
});
export const ExpandableContract = memo(({ ContractNumber: contractNumber, RegNumber: regNumber, ProductAndAgreementCode: productAndAgreementCode, Object: object, ObjectOnly: objectOnly, Customer: customer, ResidualValue: residualValue, CarPrice: carPrice, TermsCount: termsCount, StartTerm: startTerm, Terms: terms, Warnings: warnings, typeOfChange, TermAmount: termAmount, ServiceAmount: serviceAmount, ProductId: productId }) => {
    const settings = useResource(ChangeContractSettingsEndpoint, {});
    const intl = useIntl();
    const navigate = useNavigate();
    const [typesOfChange, products] = useResource([TypesOfChangeEndpoint, typeOfChange == null ? {} : null], [
        LegalProductsEndpoint,
        {
            productFromContract: productAndAgreementCode
        }
    ]);
    var termsSelectable = useMemo(() => terms?.map(t => ({ value: t.Value, key: t.Key })) ?? [], [terms]);
    termsSelectable = termsSelectable.slice(0, 3);
    const typesOfChangeSelectable = useMemo(() => typesOfChange?.map(t => ({ value: t.Text, key: t.Value })) ?? [], [typesOfChange]);
    const productCode = products.find(p => p.ProductId == productId)
        ?.ProductCodeExternal;
    const initialValues = useMemo(() => ({
        product: products && products.length === 1 ? products[0].ProductId : null,
        startTerm: startTerm,
        typeOfChange: typeOfChange
    }), [products, startTerm, typeOfChange]);
    const [errorMessage, setErrorMessage] = useState("");
    return (_jsx(Formik, { initialValues: initialValues, onSubmit: async (v) => {
            const TOCData = {
                contractNumber: contractNumber,
                regNum: regNumber,
                startDate: v.startTerm,
                typeOfChange: Number(v.typeOfChange)
            };
            const TOCResponse = await typeOfChangeChanged(TOCData);
            if (TOCResponse.ok) {
                // typeOfChangeChanged endpoint can change startTerm, but won't in most cases. Use the resulting start term here either way.
                const startTerm = TOCResponse.json().startTerm;
                const data = {
                    productId: Number(productId),
                    typeOfChange: v.typeOfChange != null ? Number(v.typeOfChange) : null,
                    startTerm: startTerm
                };
                const res = await transformContractToApplication(data);
                if (res.Success) {
                    // .. means according to _route_ hierarchy, _not_ actual path, navigate accepts parameter to set actual path
                    //setContractLoaded(productCode!)
                    navigate(`../form/product/${productCode}?initialize=false`);
                    navigate(0);
                }
                else {
                    setErrorMessage(res.ErrorMessage.length > 0
                        ? res.ErrorMessage
                        : "Something went wrong");
                }
                return Promise.resolve();
            }
            return Promise.reject();
        }, enableReinitialize: true, validationSchema: ChangeContractValidation, children: ({ isSubmitting, submitForm, resetForm, dirty, values, isValid, status = {} }) => {
            return (_jsxs(_Fragment, { children: [_jsx("div", { children: _jsxs(Grid, { container: true, spacing: 3, justifyContent: "space-between", children: [_jsxs(Grid, { xs: 12, md: 6, item: true, children: [_jsx(LabeledProperty, { label: intl.formatMessage(messages.contractNumber), value: contractNumber }), _jsx(LabeledProperty, { label: intl.formatMessage(messages.productCode), value: productCode }), _jsx(LabeledProperty, { label: intl.formatMessage(messages.customerName), value: customer }), _jsx(LabeledProperty, { label: intl.formatMessage(messages.regNumber), value: regNumber }), _jsx(LabeledProperty, { label: intl.formatMessage(messages.objectDescription), value: objectOnly }), termAmount > 0 ? (_jsx(LabeledProperty, { label: intl.formatMessage(messages.termAmount), value: _jsx(FormattedNumber, { format: "currency", value: termAmount }) })) : null, serviceAmount > 0 ? (_jsx(LabeledProperty, { label: intl.formatMessage(messages.serviceAmount), value: _jsx(FormattedNumber, { format: "currency", value: serviceAmount }) })) : null, warnings != null && warnings.length > 0 ? (_jsx(LabeledProperty, { label: intl.formatMessage(messages.warnings), value: _jsx(_Fragment, { children: warnings.map(w => (_jsx("div", { children: w }))) }) })) : null] }), _jsx(Grid, { xs: 12, md: 4, item: true, children: _jsx(Form, { children: settings.ShowTypeOfChange &&
                                            typesOfChangeSelectable.length > 0 &&
                                            typeOfChange == null ? (_jsx(Field, { name: "typeOfChange", label: intl.formatMessage(messages.typeOfChange), component: MuiAutocompleteField, margin: "normal", variant: "outlined", 
                                            // disabled={isRegistered() || errorLoading || loading}
                                            fullWidth: true, InputLabelProps: { shrink: true }, errorDisplay: ErrorDisplay.Touched, items: typesOfChangeSelectable })) : null }) })] }) }), _jsx(Typography, { color: !status.error ? "textPrimary" : "error", children: status.error || status.message || " " }), _jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => resetForm(), disabled: isSubmitting || !dirty, children: intl.formatMessage(messages.cancel) }), _jsx(SubmitButton, { isSubmitting: isSubmitting, onClick: () => submitForm(), disabled: !isValid }), _jsx(Typography, { color: !errorMessage ? "textPrimary" : "error", children: errorMessage })] })] }));
        } }));
});
const SubmitButton = ({ isSubmitting, onClick, disabled }) => {
    const intl = useIntl();
    const content = (_jsx(_Fragment, { children: isSubmitting ? (_jsx(CircularProgress, { size: 20 })) : (intl.formatMessage(messages.next)) }));
    return (_jsx(Button, { onClick: onClick, variant: "outlined", color: "secondary", disabled: isSubmitting || disabled, children: content }));
};
//# sourceMappingURL=ExpandableContract.js.map